/**
 * Where the control room lives, and how it is addressed.
 *
 * The PATHNAME decides which shell mounts (`/admin` → AdminApp, anything else →
 * the consumer App) — decided in main.tsx before React renders, the same way
 * `/invite` and the provider-return paths are recognised. Inside the panel the
 * HASH picks the section, exactly like `#/home` does in the consumer app, so
 * every link is a plain `<a href>` and Back walks the real sequence.
 *
 * Caddy's `try_files` and vite's SPA fallback already serve index.html for any
 * non-api path, so `/admin` needs no server change (web/README.md §2).
 */

export type AdminSection = 'overview' | 'review' | 'threats' | 'scores' | 'articles';

export interface AdminRoute {
  section: AdminSection;
  /** The second path segment, decoded, verbatim — the screen validates it. */
  id: string | null;
}

const SECTIONS: readonly AdminSection[] = ['overview', 'review', 'threats', 'scores', 'articles'];

/** `/admin`, `/admin/`, or a deeper `/admin/…` — never a lookalike prefix. */
export function isAdminPath(pathname: string): boolean {
  return /^\/admin(\/|$)/.test(pathname);
}

export function parseAdminHash(hash: string): AdminRoute {
  const path = hash.replace(/^#\/?/, '').replace(/\/+$/, '');
  const [first = '', second] = path.split('/');
  const section = SECTIONS.find((candidate) => candidate === first);
  if (section === undefined) return { section: 'overview', id: null };
  if (second === undefined || second === '') return { section, id: null };
  let id: string;
  try {
    id = decodeURIComponent(second);
  } catch {
    id = second;
  }
  return { section, id };
}

export function adminHash(section: AdminSection, id?: string): string {
  return id === undefined ? `#/${section}` : `#/${section}/${encodeURIComponent(id)}`;
}
